import { useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sendEmail } from "@/utils/email/api";
import { uploadReceiptPdf } from "@/utils/pdf/uploadReceipt";
import { shortenUrl } from "@/utils/shortLink";
import { convertBankDetails, getReviewLinkUrl } from "@/utils/paymentHelpers";
import { createOrUpdateCustomer } from "@/utils/customerUtils";
import { renderPaymentRequestTemplate } from "@/utils/paymentTemplate";
import { loadPaymentSettings } from "@/hooks/usePaymentSettings";
import { getDocumentMeta, type DocumentType } from "@/utils/documentMeta";

interface SmsPaymentHandlerProps {
  form: any;
  businessProfile: any;
  watchIncludeReviewLink: boolean;
  watchIncludeAddress: boolean;
  selectedReviewPlatform: string;
  onLinkGenerated?: () => void;
}

interface SendOptions {
  paymentId?: string;
  skipPersistence?: boolean;
  documentType?: DocumentType;
  includePdf?: boolean;
  paymentMethod?: string;
  customerPaymentStatus?: string;
}

export const useSmsPaymentHandler = ({
  form,
  businessProfile,
  watchIncludeReviewLink,
  watchIncludeAddress,
  selectedReviewPlatform,
  onLinkGenerated,
}: SmsPaymentHandlerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSendingSms, setIsSendingSms] = useState(false);

  const handleSendPaymentSms = async (options: SendOptions = {}) => {
    const docType: DocumentType = options.documentType ?? "invoice";
    const meta = getDocumentMeta(docType);
    setIsSendingSms(true);
    try {
      const data = form.getValues();
      if (!data.customerPhone) {
        toast({
          title: "Error",
          description: "Please provide a customer phone number to send SMS",
          variant: "destructive",
        });
        return;
      }

      const amount = parseFloat(data.amount || "0");
      const description = data.description || "";
      const currency = data.currency || "GBP";
      const businessName = businessProfile?.businessName || "Our Business";
      const paymentId = options.paymentId ?? crypto.randomUUID();
      const formattedBankDetails = convertBankDetails(businessProfile?.bankDetails);

      let reviewLinkUrl: string | undefined;
      if (watchIncludeReviewLink && businessProfile) {
        const platformToUse = selectedReviewPlatform || businessProfile.primary_review_link;
        reviewLinkUrl = getReviewLinkUrl(platformToUse, businessProfile);
      }

      // Compute due date only when the user configured a non-zero offset.
      const pdfSettings = loadPaymentSettings(user?.id);
      let computedDueDate: string | undefined;
      if (docType === "invoice" && (pdfSettings.paymentDueDays ?? 0) > 0) {
        const d = new Date();
        d.setDate(d.getDate() + Number(pdfSettings.paymentDueDays));
        computedDueDate = d.toLocaleDateString("en-GB");
      }

      // Always generate the PDF so it can be uploaded to the receipts bucket
      // (used by the "View Invoice/Receipt/Estimate" button on the customer card).
      // `includePdf` only controls whether to include the short PDF link in the SMS body.
      const includePdf = options.includePdf ?? true;
      let receiptUrl: string | null = null;
      try {
        const pdfBase64 = await meta.generatePdf({
          receiptNumber: Math.floor(Math.random() * 100000).toString(),
          date: new Date().toLocaleDateString("en-GB"),
          amount,
          currency,
          description,
          customerName: data.customerName,
          customerEmail: data.customerEmail,
          customerPhone: data.customerPhone,
          customerAddress: watchIncludeAddress
            ? { street: data.street, city: data.city, postcode: data.postcode, country: data.country }
            : undefined,
          businessProfile,
          bankDetails: formattedBankDetails,
          includeAddress: watchIncludeAddress,
          paymentMethod: options.paymentMethod,
          dueDate: computedDueDate,
          receiptSettings: pdfSettings,
        });

        if (user?.id) {
          const longUrl = await uploadReceiptPdf(pdfBase64, user.id, paymentId);
          if (includePdf && longUrl) {
            receiptUrl = await shortenUrl(longUrl, user.id);
          }
        }
      } catch (err) {
        console.error("Error preparing PDF for SMS:", err);
      }


      const savedSettings = loadPaymentSettings(user?.id);
      const template = meta.template(savedSettings);
      const smsMessage = renderPaymentRequestTemplate(template, {
        customerName: data.customerName,
        businessName,
        amount,
        currency,
        description,
        accountName: formattedBankDetails.accountName,
        accountNumber: formattedBankDetails.accountNumber,
        sortCode: formattedBankDetails.sortCode,
        dueDays: savedSettings.paymentDueDays,
      });

      let finalSmsMessage = smsMessage;
      const allowReviewLink = docType === "invoice" || docType === "receipt";
      if (allowReviewLink && reviewLinkUrl) finalSmsMessage += `\n\nLeave us a review: ${reviewLinkUrl}`;
      if (receiptUrl) finalSmsMessage += `\n\nPDF: ${receiptUrl}`;

      const { data: smsResult, error: smsError } = await supabase.functions.invoke("send-sms", {
        body: { phoneNumber: data.customerPhone, message: finalSmsMessage, sendType: "first_request" },
      });

      // Quota-exceeded surfaces as a non-2xx response — invoke() returns it via `error`
      const errorBody = (smsError as any)?.context?.body ?? smsResult;
      const errorCode =
        smsResult?.error ||
        (typeof errorBody === "string" ? (() => { try { return JSON.parse(errorBody).error; } catch { return null; } })() : errorBody?.error);

      if (errorCode === "sms_quota_exceeded") {
        const err = new Error("sms_quota_exceeded") as Error & { code?: string };
        err.code = "sms_quota_exceeded";
        throw err;
      }
      if (smsError) throw new Error(smsError.message || "Failed to send SMS");
      if (!smsResult?.success) throw new Error(smsResult?.error || "Failed to send SMS");

      if (!options.skipPersistence) {
        const { error: paymentError } = await supabase.from("payment_links").insert([{
          id: paymentId,
          user_id: user?.id,
          amount,
          currency,
          status: meta.status,
          description,
          customer_email: data.customerEmail || null,
          customer_name: data.customerName || null,
          payment_type: meta.paymentType,
          review_link_included: watchIncludeReviewLink,
          provider: meta.provider,
          url: "#",
        }]);
        if (paymentError) console.error("Error creating payment record:", paymentError);

        if (data.customerName) {
          await createOrUpdateCustomer(
            data.customerEmail || "",
            data.customerName,
            paymentId,
            user?.id,
            watchIncludeAddress ? data.street : undefined,
            watchIncludeAddress ? data.postcode : undefined,
            data.customerPhone,
            options.customerPaymentStatus,
            watchIncludeAddress ? data.city : undefined,
          );
        }

        toast({ title: "Success", description: `${meta.label} sent via SMS to ${data.customerPhone}` });

        form.reset({
          amount: "", currency: "GBP", description: "",
          customerEmail: "", customerPhone: "", customerName: "",
          includeAddress: false, includePdfReceipt: false,
          includeReviewLink: false, selectedReviewLink: "",
          street: "", city: "", postcode: "", country: "",
        });
        onLinkGenerated?.();
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      toast({
        title: "Error",
        description: `Failed to send SMS: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    } finally {
      setIsSendingSms(false);
    }
  };

  return { handleSendPaymentSms, isSendingSms };
};
